/**
 * CRM Events campaign -> FE `Promotion` mapping (shared by the Operations list
 * hook and the promotion-detail hook so both resolve an item identically).
 *
 * Reads the confirmed `OperationsCampaignItem` fields DEFENSIVELY (every field is
 * optional) and always returns a valid `Promotion`:
 *   image_url|image -> `image`        (was mis-read as `image` before -> broken thumb)
 *   start_at/end_at -> `period`       (compact "D Mon YYYY - D Mon YYYY" label)
 *   benefit_scores  -> `benefits`     (+N Conversion / Monetization / Retention chips)
 *   ui_status|status-> `status`       (backend-resolved status preferred)
 *   type            -> `category`     (Acquisition/Promo -> filter-chip value)
 *   end_at          -> `expiresAt`    (epoch ms; a past deadline reads as ended)
 */
import type { CampaignStatus, Promotion, PromotionBenefit } from '../../../mock-data/types';
import type { OperationsCampaignItem } from './crm-events';

const KNOWN_STATUSES: readonly CampaignStatus[] = ['suggestion', 'running', 'paused', 'ended'];

const asString = (value: unknown): string | undefined =>
  typeof value === 'string' && value ? value : undefined;

/** Backend-resolved `ui_status` wins over the raw `status`; unknown values fall back to running. */
const toStatus = (item: OperationsCampaignItem): CampaignStatus => {
  const raw = (asString(item.ui_status) ?? asString(item.status))?.toLowerCase();
  return (KNOWN_STATUSES as readonly string[]).includes(raw ?? '') ? (raw as CampaignStatus) : 'running';
};

/** `Acquisition`/`Promo` -> the promotion filter-chip category; anything else -> retention. */
const toCategory = (item: OperationsCampaignItem): string => {
  const type = asString(item.type)?.toLowerCase();
  if (type === 'acquisition') return 'acquisition';
  if (type === 'promo') return 'promo';
  return 'retention';
};

/** Parse an ISO timestamp to epoch ms, or `undefined` when absent/unparseable. */
const toEpoch = (iso: string | undefined): number | undefined => {
  if (!iso) return undefined;
  const ms = Date.parse(iso);
  return Number.isNaN(ms) ? undefined : ms;
};

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
// The campaign window bounds are UTC ISO timestamps, so read the date in UTC (not
// the browser's local zone) — otherwise the label shifts a day for users far from
// UTC. Matches the area's other API-date formatters (campaign-performance/stats).
const formatDate = (iso: string | undefined): string | undefined => {
  const ms = toEpoch(iso);
  if (ms == null) return undefined;
  const d = new Date(ms);
  return `${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
};

/** "D Mon YYYY - D Mon YYYY" (single side when only one bound is present, `''` when neither). */
export const formatCampaignPeriod = (startAt?: string, endAt?: string): string => {
  const start = formatDate(startAt);
  const end = formatDate(endAt);
  if (start && end) return `${start} - ${end}`;
  return start ?? end ?? '';
};

/** The +N benefit chips, built from `benefit_scores` (falls back to bare `benefits` labels). */
export const toBenefits = (item: OperationsCampaignItem): PromotionBenefit[] => {
  const scores = item.benefit_scores;
  if (scores) {
    const order: ReadonlyArray<{ id: keyof typeof scores; label: string }> = [
      { id: 'conversion', label: 'Conversion' },
      { id: 'monetization', label: 'Monetization' },
      { id: 'retention', label: 'Retention' },
    ];
    return order
      .filter((b) => typeof scores[b.id] === 'number')
      .map((b) => ({ id: b.id, label: b.label, value: `+${scores[b.id]}` }));
  }
  const labels = Array.isArray(item.benefits) ? item.benefits : [];
  return labels.map((label, i) => ({ id: `benefit-${i}`, label, value: '' }));
};

/**
 * Map a CRM Events campaign item onto a browser-card `Promotion`. `index` seeds a
 * stable fallback id for the (never-observed) item with no id.
 */
export const toPromotion = (item: OperationsCampaignItem, index: number): Promotion => ({
  id: asString(item.id) ?? asString(item.campaign_id) ?? `operations-promotion-${index}`,
  period: formatCampaignPeriod(item.start_at, item.end_at),
  title: asString(item.title) ?? asString(item.name) ?? 'Promotion',
  image: asString(item.image_url) ?? asString(item.image) ?? '',
  status: toStatus(item),
  category: toCategory(item),
  benefits: toBenefits(item),
  expiresAt: toEpoch(item.end_at),
});
